import { Language, Question_Type, useQuestionQuery, useSkillBasicQuery } from "@generated/graphql";
import { InfoOutlined } from "@mui/icons-material";
import { Stack, Tooltip } from "@mui/material";
import CircularLoading from "components/CircularLoading";
import PageHeader from "components/PageHeader";
import BackPageButton from "components/buttons/BackPageButton";
import { BackPageButtonSearchParamName } from "components/buttons/BackPageButton/types";
import Form from "components/form/Form";
import { Competency_Map_Tabs_Values, GRADE_OPTIONS, QUESTION_TYPES, QUESTIONS_TABS_VALUES } from "constants/global";
import ROUTES from "constants/routes";
import { escapeHtmlCharsInCodeBlock, replaceNLSybmolWithBr } from "helpers/CKEditor";
import { formatNumber } from "helpers/index";
import useIsCreate from "hooks/useIsCreate";
import useSetTabTitle from "hooks/useSetTabTitle";
import ViewGridLayout from "layout/ViewGridLayout";
import qs from "qs";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import Info from "./components/Info";
import LeftColumn from "./components/LeftColumn";
import { QuestionInputs } from "./types";

const QuestionView = () => {
  const isCreate = useIsCreate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as {
    skillId?: string;
    [BackPageButtonSearchParamName.COMPETENCY_MAP_ID]?: string;
  };

  const { skillId: skillIdParam, competencyMapId } = params;
  const [isEditing, setIsEditing] = useState<boolean>(true);

  const title = isCreate ? "Создание вопроса" : "Редактирование вопроса";

  useSetTabTitle(title);

  const { data: questionData, loading: questionLoading } = useQuestionQuery({
    variables: {
      id: id!,
    },
    fetchPolicy: "cache-and-network",
  });
  const question = questionData?.question;
  const skillId = question?.skill.id || skillIdParam;

  const { data: skillData } = useSkillBasicQuery({
    variables: {
      skillId: skillId!,
    },
  });
  const skill = skillData?.skill;

  const getRecommendedTime = () => {
    if (question?.recommendedTime) {
      const recommendedTimeToMinutes = Math.ceil(question.recommendedTime / 60);
      return formatNumber(["минута", "минуты", "минут"], recommendedTimeToMinutes);
    }
    return undefined;
  };

  const form = useForm<QuestionInputs>({
    values: {
      knowledgeBlock: {
        title: skill?.knowledgeBlock.title || "",
      },
      title: skill?.title || "",
      time: String(question ? question.time / 60 : 1),
      criterion: question?.criterion?.map((value) => ({ value })) || [],
      recommendedTime: getRecommendedTime() || "",
      text: replaceNLSybmolWithBr(escapeHtmlCharsInCodeBlock(question?.text || "")),
      grade: question ? GRADE_OPTIONS.find((grade) => grade.id === question?.grade) || null : null,
      type: question ? QUESTION_TYPES.find((type) => type.id === question?.type) || null : null,
      subtype: question?.subtype || "",
      language: question?.language || Language.Ru,
      partsInput:
        question?.parts?.map(({ id: partId, content, isCorrect }) => ({ id: partId, content, isCorrect })) || [],
      referenceAnswer: question?.referenceAnswer || "",
    },
  });

  const { watch } = form;
  const questionType = watch("type");
  const isMultiChoiceQuestion = questionType?.id === Question_Type.MultipleChoiceQuestions;

  const getQuestionsTabValue = (action: "submit" | "cancel") => {
    if (action === "submit") return QUESTIONS_TABS_VALUES.unconfirmed;

    if (isCreate || question?.confirmation) return QUESTIONS_TABS_VALUES.confirmed;

    return QUESTIONS_TABS_VALUES.unconfirmed;
  };

  const getSkillBackLocation = (action: "submit" | "cancel") => ({
    pathname: `/${ROUTES.SKILL}/view/${skillId}`,
    search: qs.stringify({
      tab: getQuestionsTabValue(action),
    }),
  });

  const getBackButtonSearch = () => {
    if (competencyMapId) {
      return qs.stringify({
        tab: Competency_Map_Tabs_Values.questionsList,
        questionId: question?.id,
      });
    }

    return undefined;
  };

  if (questionLoading) return <CircularLoading />;

  return (
    <>
      <BackPageButton
        defaultValue={{
          text: `Навык${skill?.title ? ` «${skill?.title}»` : ""} `,
          link: getSkillBackLocation("cancel"),
        }}
        getSearch={getBackButtonSearch}
      />
      <Form form={form}>
        <Stack
          spacing={2.5}
          useFlexGap>
          <PageHeader
            title={
              <Stack
                direction='row'
                alignItems='center'
                gap={2}>
                {title}
                {!isCreate && (
                  <Tooltip
                    title='При&#160;изменении подтвержденного вопроса&#160;— вопрос будет перенаправлен в&#160;список
                неподтвержденных вопросов. Для&#160;использования вопроса в&#160;рамках тестирований необходимо
                подтвердить данный вопрос.'>
                    <InfoOutlined color='info' />
                  </Tooltip>
                )}
              </Stack>
            }
          />
          <ViewGridLayout
            containerSx={{ flexWrap: "nowrap", flexDirection: { md: "row", xs: "column-reverse" } }}
            LeftColumn={
              <LeftColumn
                isEditing={isEditing}
                skillId={skillId}
                isMultiChoiceQuestion={isMultiChoiceQuestion}
              />
            }
            RightColumn={
              <Info
                question={question}
                skill={skill}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            }
          />
        </Stack>
      </Form>
    </>
  );
};

export default QuestionView;
